// O AUTOCOMPLETAR DAS PASTILHAS DA BUSCA.
//
// Apontamento 43 da revisão de UX (sugestão, com mockup): "hoje os chips
// (de:, para:, tem:anexo, antes:, marcador:) só aparecem; o usuário precisa
// saber a sintaxe e digitar o valor exato".
//
// O "valor exato" era o problema real: `marcador:financeiro` não acha o
// marcador `Financeiro`, porque keyword do IMAP é sensível a caixa. Quem
// digita não tem como saber a grafia, e a busca volta vazia sem dizer por quê.
//
// ESTA LÓGICA É PURA E FICA SEPARADA DA TELA por um motivo prático: o teste
// deste projeto roda em ambiente `node`, sem DOM. Regra de interface que mora
// dentro do JSX não tem como ser verificada, e esta tem casos de borda que
// erram em silêncio — cursor no meio do texto, aspas, prefixo repetido.

/** Os prefixos que oferecem sugestão, e o que cada um sugere. */
export type FonteDeSugestao = "marcador" | "contato" | "data";

const FONTES: Record<string, FonteDeSugestao> = {
  "de:": "contato",
  "from:": "contato",
  "para:": "contato",
  "to:": "contato",
  "marcador:": "marcador",
  "label:": "marcador",
  "antes:": "data",
  "before:": "data",
  "depois:": "data",
  "after:": "data",
};

/** O pedaço do texto que está sendo editado agora. */
export type EmEdicao = {
  /** `marcador:`, `de:`… com os dois-pontos. */
  prefixo: string;
  /** O que já foi digitado depois do prefixo, sem aspas. */
  valor: string;
  /** Onde o pedaço começa no texto, para substituí-lo sem tocar no resto. */
  inicio: number;
  fonte: FonteDeSugestao;
};

/**
 * Qual filtro está sendo digitado na posição do cursor.
 *
 * O CURSOR, e não o fim do texto: quem volta para corrigir o primeiro de
 * `marcador:fin de:ana` espera sugestão de marcador, não de contato. Usar o
 * fim do texto ofereceria a lista errada exatamente quando a pessoa está
 * consertando algo.
 *
 * Devolve `null` quando o que está sob o cursor não é um filtro com sugestão
 * — texto livre, `tem:anexo` (que não tem valor a escolher) ou prefixo que o
 * servidor não conhece.
 */
export function filtroEmEdicao(termo: string, cursor: number): EmEdicao | null {
  const posicao = Math.max(0, Math.min(cursor, termo.length));
  // O pedaço começa depois do último espaço ANTES do cursor — mas espaço
  // dentro de aspas não separa, senão `de:"Ana Paula"` seria cortado no meio
  // e a sugestão passaria a ser sobre `Paula"`.
  let inicio = 0;
  let dentro: string | null = null;
  for (let i = 0; i < posicao; i++) {
    const c = termo[i]!;
    if (dentro) {
      if (c === dentro) dentro = null;
      continue;
    }
    if (c === '"' || c === "'") {
      dentro = c;
      continue;
    }
    if (c === " " || c === "\t") inicio = i + 1;
  }

  const pedaco = termo.slice(inicio, posicao);
  const dois = pedaco.indexOf(":");
  if (dois <= 0) return null;

  const prefixo = pedaco.slice(0, dois + 1).toLowerCase();
  const fonte = FONTES[prefixo];
  if (!fonte) return null;

  return {
    prefixo: pedaco.slice(0, dois + 1),
    valor: pedaco.slice(dois + 1).replace(/^["']|["']$/g, ""),
    inicio,
    fonte,
  };
}

/**
 * Põe o valor escolhido no lugar do filtro em edição.
 *
 * Devolve o texto novo e onde deixar o cursor. O cursor importa: largá-lo no
 * fim do texto faria a pessoa que corrigia o primeiro filtro de três voltar
 * do fim a cada escolha.
 *
 * Valor com espaço sai entre aspas, que é a forma que o servidor entende
 * (`de:"Ana Paula"`). Sem elas, o espaço quebraria o filtro em dois pedaços e
 * "Paula" viraria texto de busca.
 */
export function aplicarSugestao(
  termo: string,
  cursor: number,
  escolha: string,
): { termo: string; cursor: number } {
  const edicao = filtroEmEdicao(termo, cursor);
  if (!edicao) return { termo, cursor };

  const valor = /[\s]/.test(escolha) ? `"${escolha}"` : escolha;
  const antes = termo.slice(0, edicao.inicio);
  // O resto do texto continua intacto, a partir do cursor: só o pedaço que
  // estava sendo digitado é trocado.
  const depois = termo.slice(Math.min(cursor, termo.length));
  // Espaço no fim para o próximo filtro já poder ser digitado — menos quando
  // o texto que segue já começa com um, senão sobrariam dois.
  const sufixo = depois.startsWith(" ") ? "" : " ";
  const montado = antes + edicao.prefixo + valor + sufixo;

  // O cursor fica DEPOIS do espaço, pronto para o filtro seguinte, e não no
  // fim do texto: quem corrigia o primeiro de três filtros voltaria do fim a
  // cada escolha.
  return { termo: montado + depois, cursor: montado.length };
}

/**
 * Anda na lista com as setas, dando a volta.
 *
 * Dar a volta é o que permite chegar ao último item com uma tecla só. Lista
 * vazia devolve 0 em vez de -1: índice negativo viraria `itens[-1]`, que é
 * `undefined`, e o Enter não faria nada sem dizer por quê.
 */
export function proximoIndice(atual: number, quantos: number, passo: number): number {
  if (quantos <= 0) return 0;
  return (atual + passo + quantos) % quantos;
}
